import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { useSiteImages } from "@/hooks/useSiteImages";

export const Hero = () => {
  const imgs = useSiteImages();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [failed, setFailed] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  return (
    <section data-testid="hero-section" className="pt-12 md:pt-20 pb-0 bg-[#F5F5F7]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          data-testid="hero-overline"
          className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6"
        >
          Pure Water. Born from the Sky.
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          data-testid="hero-heading"
          className="font-display text-5xl md:text-7xl lg:text-[6rem] leading-[1.05] tracking-tighter font-bold text-[#1D1D1F]"
        >
          Born from Air.
          <br />
          Perfected by Nature.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="mt-8 text-lg md:text-xl leading-relaxed text-[#86868B] max-w-2xl mx-auto"
        >
          Nyla Air Water is not drawn from the ground — it is gathered gently from the sky,
          purified with care, and enriched with nature's essential minerals.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <Link
            to="/products"
            data-testid="hero-cta-products"
            className="px-7 py-3.5 rounded-full bg-[#1D1D1F] text-white text-sm font-medium hover:bg-black active:scale-95 transition-[background-color,transform] duration-200"
          >
            Discover Products
          </Link>
          <Link
            to="/innovation"
            data-testid="hero-cta-technology"
            className="px-7 py-3.5 rounded-full text-sm font-medium text-[#2C3E35] hover:bg-[#2C3E35]/5 active:scale-95 transition-[background-color,transform] duration-200"
          >
            Explore Our Craft →
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="mt-16 md:mt-24 max-w-7xl mx-auto px-6 md:px-12"
      >
        <div className="relative rounded-3xl overflow-hidden group">
          {failed ? (
            <img
              data-testid="hero-video-fallback"
              src={imgs.hero_poster}
              alt="Crystal-clear water droplet"
              className="w-full h-[380px] md:h-[620px] object-cover"
            />
          ) : (
            <>
              <video
                key={imgs.hero_video}
                ref={videoRef}
                data-testid="hero-video"
                src={imgs.hero_video}
                poster={imgs.hero_poster}
                autoPlay
                muted
                loop
                playsInline
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onError={() => setFailed(true)}
                className="w-full h-[380px] md:h-[620px] object-cover"
              />
              <button
                data-testid="hero-video-toggle"
                onClick={toggle}
                aria-label={playing ? "Pause video" : "Play video"}
                className="absolute bottom-5 right-5 w-12 h-12 rounded-full bg-black/40 backdrop-blur-md text-white flex items-center justify-center hover:bg-black/60 active:scale-95 transition-[background-color,transform] duration-200"
              >
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </section>
  );
};
